import { useCallback, useEffect, useState } from "react";

import {
  api,
  ApiError,
  Operator,
  PendingInvite,
  RevokedOperator,
} from "../lib/api";

// Admin → Operators page. Lists active operators the admin has invited,
// pending invites that haven't been accepted yet, and an inline form to
// invite a new one. The invite link is shown inline with a Copy button —
// no SMTP, the admin shares it via Slack/Teams/email manually.
export default function AdminOperators() {
  const [operators, setOperators] = useState<Operator[]>([]);
  const [revoked, setRevoked] = useState<RevokedOperator[]>([]);
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [copyId, setCopyId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setErr(null);
    try {
      const [ops, rev, invs] = await Promise.all([
        api.listOperators(),
        api.listRevokedOperators(),
        api.listOperatorInvites(),
      ]);
      setOperators(ops);
      setRevoked(rev);
      setInvites(invs);
    } catch (e) {
      setErr(
        e instanceof ApiError ? e.message : "could not load operators",
      );
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await api.inviteOperator(inviteEmail.trim().toLowerCase());
      setInviteEmail("");
      await refresh();
    } catch (ex) {
      setErr(ex instanceof ApiError ? ex.message : "could not invite");
    } finally {
      setBusy(false);
    }
  }

  async function cancel(inviteId: string) {
    setBusy(true);
    setErr(null);
    try {
      await api.cancelOperatorInvite(inviteId);
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "could not cancel invite");
    } finally {
      setBusy(false);
    }
  }

  async function revoke(userId: string) {
    if (!confirm("Revoke this operator? Their sessions stay; they can no longer log in.")) {
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.revokeOperator(userId);
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "could not revoke operator");
    } finally {
      setBusy(false);
    }
  }

  async function reinstate(userId: string, email: string) {
    if (
      !confirm(
        `Reinstate ${email} as an operator? They'll regain operator powers ` +
          "and will be able to moderate any session in your chain.",
      )
    ) {
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.reinstateOperator(userId);
      await refresh();
    } catch (e) {
      setErr(
        e instanceof ApiError ? e.message : "could not reinstate operator",
      );
    } finally {
      setBusy(false);
    }
  }

  async function copyLink(url: string, key: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopyId(key);
      setTimeout(() => setCopyId(null), 1500);
    } catch {
      setErr("Clipboard permission denied — copy the link manually");
    }
  }

  return (
    <main>
      <h1>Operators</h1>
      <p className="meta">
        Operators create and run sessions. Invite by email — the invitee
        signs in via OAuth and is automatically scoped under you.
      </p>

      <section className="card" style={{ marginTop: "1rem" }}>
        <h2>Invite operator</h2>
        <form
          onSubmit={submit}
          className="row"
          style={{ gap: "0.5rem", flexWrap: "wrap" }}
        >
          <input
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="email@example.com"
            required
            style={{ flex: 1, minWidth: "220px" }}
          />
          <button type="submit" disabled={busy || !inviteEmail.trim()}>
            Send invite
          </button>
        </form>
        {err && (
          <p className="error" role="alert" style={{ marginTop: "0.5rem" }}>
            {err}
          </p>
        )}
      </section>

      {invites.length > 0 && (
        <section style={{ marginTop: "1.5rem" }}>
          <h2>Pending invites ({invites.length})</h2>
          <ul style={{ listStyle: "none", padding: 0 }}>
            {invites.map((inv) => (
              <li
                key={inv.invite_id}
                className="card"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  flexWrap: "wrap",
                }}
              >
                <div style={{ flex: 1, minWidth: "200px" }}>
                  <div style={{ fontWeight: 600 }}>{inv.email}</div>
                  <div className="meta" style={{ fontSize: "0.8rem" }}>
                    Expires {new Date(inv.expires_at).toLocaleString()}
                  </div>
                </div>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => copyLink(inv.invite_url, inv.invite_id)}
                >
                  {copyId === inv.invite_id ? "Copied" : "Copy link"}
                </button>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => cancel(inv.invite_id)}
                  disabled={busy}
                >
                  Cancel
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section style={{ marginTop: "1.5rem" }}>
        <h2>Active operators ({operators.length})</h2>
        {operators.length === 0 ? (
          <p className="meta">
            No operators yet. Invite someone above to start delegating
            session management.
          </p>
        ) : (
          <ul style={{ listStyle: "none", padding: 0 }}>
            {operators.map((op) => (
              <li
                key={op.user_id}
                className="card"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  flexWrap: "wrap",
                }}
              >
                <div style={{ flex: 1, minWidth: "200px" }}>
                  <div style={{ fontWeight: 600 }}>{op.email}</div>
                  <div className="meta" style={{ fontSize: "0.8rem" }}>
                    Joined {new Date(op.created_at).toLocaleString()}
                  </div>
                </div>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => revoke(op.user_id)}
                  disabled={busy}
                  style={{ borderColor: "var(--danger, #dc2626)", color: "var(--danger, #dc2626)" }}
                >
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {revoked.length > 0 && (
        <section style={{ marginTop: "1.5rem" }}>
          <h2>Revoked ({revoked.length})</h2>
          <p className="meta">
            These operators can't log in. Reinstate restores their operator
            powers, including moderating any session in your chain — no
            separate moderator invite needed.
          </p>
          <ul style={{ listStyle: "none", padding: 0 }}>
            {revoked.map((op) => (
              <li
                key={op.user_id}
                className="card"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  flexWrap: "wrap",
                  opacity: 0.85,
                }}
              >
                <div style={{ flex: 1, minWidth: "200px" }}>
                  <div style={{ fontWeight: 600 }}>{op.email}</div>
                  <div className="meta" style={{ fontSize: "0.8rem" }}>
                    Joined {new Date(op.created_at).toLocaleString()} · revoked
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => reinstate(op.user_id, op.email)}
                  disabled={busy}
                >
                  Reinstate
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </main>
  );
}
